import {
    Box,
    Checkbox,
    FormControlLabel,
    FormHelperText,
    Grid,
    Theme,
    useMediaQuery,
} from '@material-ui/core';
import {useFormik} from 'formik';
import {Trans, useTranslation} from 'react-i18next';
import {
    Button,
    FormGroup,
    Label,
    PhoneNumberField,
} from '../..';
import i18nNamespaces from '../../../const/i18nNamespaces';
import {
    PRIVACY_POLICY_PAGE,
    TERMS_AND_CONDITIONS_PAGE,
} from '../../../const/routes';
import useStyles from './RegisterPhoneNumberFormStyles';
import {NewTabLink} from '../../common/navigation';
import useMsalActiveAccount from '../../../hooks/msal/useMsalActiveAccount';
import entraIDRegisterSchema from '../../../validators/EntraIDRegister';
import * as yup from "yup";
import {isValidPhoneNumber} from "react-phone-number-input";

export type RegisterPhoneNumberFormValues = {
    phoneNumber: string;
};

type RegisterPhoneNumberFormProps = {
    onSubmit: (values: RegisterPhoneNumberFormValues) => void;
    error: string;
};

const RegisterPhoneNumberForm = ({onSubmit, error}: RegisterPhoneNumberFormProps) => {
    const {t} = useTranslation(i18nNamespaces.AUTH);
    const isDesktop = useMediaQuery<Theme>(theme => theme.breakpoints.up('sm'));
    const {activeAccount} = useMsalActiveAccount();

    const {handleChange, handleBlur, handleSubmit, values, touched, errors} =
        useFormik({
            initialValues: {
                phoneNumber: '',
            },
            validationSchema: yup.object({
                phoneNumber: yup
                    .string()
                    .min(12, 'tooShortPhoneNumber')
                    .test('isValidNumber', 'invalidNumber', value =>
                        value ? isValidPhoneNumber(value) : true,
                    )
                    .required(t('requiredField')),
            }),
            onSubmit,
        });

    const classes = useStyles();

    const handlePhoneNumberChange = (value: string) =>
        handleChange({
            target: {
                value,
                id: 'phoneNumber',
                name: 'phoneNumber',
            },
        });

    return (
        <form onSubmit={handleSubmit}>
            <Box>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                >
                    <FormGroup className={classes.formGroup}>
                        <Label htmlFor="phoneNumber">{t('phoneNumber')}</Label>
                        <PhoneNumberField
                            id="phoneNumber"
                            name="phoneNumber"
                            onChange={handlePhoneNumberChange}
                            onBlur={handleBlur}
                            value={values.phoneNumber}
                            helperText={
                                touched.phoneNumber && t(errors.phoneNumber)
                            }
                            error={touched.phoneNumber && !!errors.phoneNumber}
                            defaultCountry="PL"
                            fullWidth={!isDesktop}
                        />
                    </FormGroup>
                </Grid>
                <Grid
                    container
                    direction="column"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                >
                    {!!error && (
                        <Grid item className={classes.formSubsection}>
                            <Box color="red" fontSize={16}>
                                {error}
                            </Box>
                        </Grid>
                    )}
                    <Grid item className={classes.submitContainer}>
                        <Button
                            variant="contained"
                            color="secondary"
                            type="submit"
                            className={classes.submitButton}
                        >
                            {t('register')}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </form>
    );
};

export default RegisterPhoneNumberForm;
